import { GridCellParams } from '@mui/x-data-grid-pro';
import Chip from '@mui/material/Chip';
import { useEffect, useState } from 'react';
import { uniq, map, groupBy, sortBy } from 'lodash';
import { Link } from 'react-router-dom';
import { v4 } from 'uuid';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { Icon, IconType } from '../../Atoms/Icon';
import { Text } from '../../Atoms/Text';
import { ComponentName, getMetaData } from '../DetailsDrawer/detailsDrawersHelpers';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { GridCustomColDef } from '../../types';
import { chipStyles, formatPercent, formatPrice, getFormattedDate } from '../../../utils/helpers';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { LinkAllocation } from '../../../store/Link';
import { getAllocationsByTypeId } from '../../../Api/Link/LinkApi';
import { BasicDataGrid } from '../Table/BasicDataGrid';
import { DetailCTARenderer } from '../UpstackDataGrid/CellRenderers';
import { DetailViewCard } from '../CardWithTitle';

interface AllocationRecord extends LinkAllocation {
  [key: string]: string | number | undefined | null | boolean;
}

const gridCols = (
  firstColumnData: { name: string; field: string; id: string },
  monthYears: string[],
  type: 'combined-assets' | 'rpm-accounts',
  id?: string
): GridCustomColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'details',
    headerName: '',
    sortable: false,
    width: 50,
    filterable: false,
    renderCell: (params: GridCellParams) => {
      if (params.row.linkId === 'Total') return null;
      const id = params.row[firstColumnData.id];
      let componentName = ComponentName.RPM_ACCOUNT;
      let type = 'rpmAccount';
      if (firstColumnData.name === 'Asset') {
        if (params.row.assetType === 'provisional_asset') {
          componentName = ComponentName.PROVISIONAL_ASSET;
          type = 'provisionalAsset';
        } else {
          componentName = ComponentName.ASSET;
          type = 'asset';
        }
      }
      return (
        <DetailCTARenderer
          data={params}
          value={''}
          valueFormatted={''}
          state={{
            id: '',
            type,
            data: params.row,
            ...getMetaData({ name: componentName, id }),
            replace: false
          }}
        />
      );
    },

    hideable: false,
    pinned: 'left',
    disableReorder: true
  },
  {
    headerName: firstColumnData.name,
    field: firstColumnData.field,
    renderCell: (params: GridCellParams) => {
      if (params.row.linkId === 'Total') return null;
      let type = 'rpm-account';
      let iconType = 'rpmAccountMed';
      if (firstColumnData.name === 'Asset') {
        if (params.row.assetType === 'provisional_asset') {
          type = 'provisional-asset';
          iconType = 'provisionalAssetMed';
        } else {
          type = 'asset';
          iconType = 'assetMed';
        }
      }
      return (
        <Link
          className="text-indigo flex items-center"
          title="Fullscreen"
          target="_blank"
          rel="noopener noreferrer"
          to={`/${type}/${params.row[firstColumnData.id]}`}>
          <Icon
            className="p-3 mr-1"
            type={iconType as IconType}
          />
          <Text
            size="sm14"
            color="indigo">
            {params?.value as string}
          </Text>
        </Link>
      );
    },
    minWidth: 150,
    flex: 1,
    type: 'singleSelect',
    filterable: false,
    sortable: false,
    valueOptions: []
  },
  {
    headerName: 'Link',
    field: 'linkId',
    sortable: false,
    width: 50,
    filterable: false,
    renderCell: (params: GridCellParams) => {
      if (params.row.linkId === 'Total') return null;
      return (
        <DetailCTARenderer
          data={params}
          value={''}
          valueFormatted={''}
          state={{
            id: v4(),
            data: params.row,
            ...getMetaData({ name: ComponentName.LINK, id: params.row.linkId }),
            replace: false
          }}
        />
      );
    }
  },
  ...monthYears.map((monthYear, index) => ({
    headerName: monthYear,
    renderHeader: () => (
      <div>
        <span>{monthYear}</span>
        {type === 'rpm-accounts' && (
          <Link
            className="text-indigo absolute right-2 group-hover:opacity-100 font-normal"
            title="Fullscreen"
            to={`/rpm-account/${id}/allocation-breakdown/${monthYear}`}>
            Breakdown
          </Link>
        )}
      </div>
    ),
    field: `monthYear${index}`,
    renderCell: (params: GridCellParams) => {
      const allocationPercent = params.row[`monthYear${index}allocationPercent`];
      const runStatus = params.row[`monthYear${index}RunStatus`];
      const isManualOverride = params.row[`monthYear${index}allocationManualOverride`];
      const className = isManualOverride ? 'bg-dark-orange text-white' : 'bg-gray-100 text-black';
      return (
        <div>
          {runStatus ? (
            <Chip
              label={runStatus}
              size="small"
              sx={chipStyles(runStatus)}
              title={runStatus}
            />
          ) : (
            <span className={`py-1 px-2 rounded-md ${className}`}>{formatPercent(allocationPercent, true)}</span>
          )}
          <span className={`float-right ${params.value && Number(params.value) < 0 ? 'text-price-red' : ''}`}>
            {formatPrice(params.value as number)}
          </span>
        </div>
      );
    },
    minWidth: 170,
    flex: 1,
    filterable: false,
    sortable: false
  }))
];

export interface AllocationMiniTableParams {
  id: string;
  type: 'combined-assets' | 'rpm-accounts';
}

export const AllocationMiniTable = ({ id, type }: AllocationMiniTableParams) => {
  const [records, setRecords] = useState<LinkAllocation[]>([]);
  const [monthYears, setMonthYears] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { setSnack } = useSnackbar();
  const firstColumnData =
    type === 'rpm-accounts'
      ? {
          name: 'Asset',
          field: 'assetNumber',
          id: 'assetUpstackId'
        }
      : {
          name: 'RPM Account',
          field: 'rpmAccountNumber',
          id: 'rpmAccountId'
        };

  const formattingData = (data: LinkAllocation[]) => {
    const sortedByRunDate = sortBy(data, (record) => new Date(record.rpmRunDate).getTime() * -1);
    const groupedData = groupBy(data, (record) => record.linkId);
    const uniqueMonthYears = uniq(map(sortedByRunDate, (record) => getFormattedDate(record.rpmRunDate, 'MMM YY')));
    const totalRecord: AllocationRecord = uniqueMonthYears.reduce((acc, monthYear, index) => {
      acc[`monthYear${index}`] = 0;
      return acc;
    }, {} as AllocationRecord);
    const formattedData = Object.keys(groupedData).map((key) => {
      const group = groupedData[key];
      const monthYearIndex = group.reduce<Record<string, LinkAllocation>>((acc, r) => {
        const formatted = getFormattedDate(r.rpmRunDate, 'MMM YY');
        if (!acc[formatted]) {
          acc[formatted] = r;
        }
        return acc;
      }, {});
      const baseRecord = { ...group[0] } as AllocationRecord; // Take the first record as the base
      uniqueMonthYears.forEach((monthYear, index) => {
        const record = monthYearIndex[monthYear];
        baseRecord[`monthYear${index}allocationPercent`] = record ? record.allocationPercent : 0;
        baseRecord[`monthYear${index}allocationManualOverride`] = record ? record.isManualOverride : false;
        baseRecord[`monthYear${index}`] = record ? record.revenueRecurringAllocated : 0; // Add allocation percentage for each month-year
        (totalRecord[`monthYear${index}`] as number) += baseRecord[`monthYear${index}`] as number; // Accumulate totals
        const runStatusKey = `monthYear${index}RunStatus`;
        const currentRunStatus = totalRecord[runStatusKey];
        const newRunStatus = record ? record.runStatus : undefined;
        if (newRunStatus && (!currentRunStatus || currentRunStatus === 'N/A')) {
          totalRecord[runStatusKey] = newRunStatus;
        }
      });
      return baseRecord;
    });
    if (formattedData.length === 0) {
      setRecords(formattedData);
      setMonthYears([]);
      return;
    }
    const sortedData = sortBy(formattedData, [firstColumnData.field]);
    setRecords([...sortedData, { ...totalRecord, linkId: 'Total' }]);
    setMonthYears(uniqueMonthYears);
  };

  const fetchData = async (id: string) => {
    try {
      setLoading(true);
      const { data, error } = await getAllocationsByTypeId(type, id);
      if (data) formattingData(data);
      if (error) setSnack({ message: error.message, type: 'error', open: true });
    } catch {
      setSnack({ message: 'Error fetching allocation data', type: 'error', open: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchData(id);
  }, [id, type]);

  const sxStyle = {
    ...DEFAULT_MINI_TABLE_SX_STYLES,
    minHeight: 'inherit',
    maxHeight: 'calc(100vh - 320px)',
    height: 'inherit',
    border: 'none',
    borderRadius: '0',
    '.MuiDataGrid-columnHeader': {
      ...DEFAULT_MINI_TABLE_SX_STYLES['.MuiDataGrid-columnHeader'],
      color: '#000000',
      fontWeight: 600
    },
    '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell, .MuiDataGrid-overlay': {
      borderRight: '1px solid #E0E0E0'
    },
    '& .MuiDataGrid-columnsContainer': {
      borderBottom: '1px solid #E0E0E0'
    },
    '.MuiDataGrid-columnSeparator': {
      display: 'none'
    },
    '.MuiDataGrid-main': {
      border: '1px solid #E0E0E0',
      borderRight: 'none'
    },
    '.MuiDataGrid-row[data-id="Total"]': {
      backgroundColor: '#F7F7F7'
    }
  };

  const gridColumns = gridCols(firstColumnData, monthYears, type, id);

  return (
    <DetailViewCard
      enableCollapse
      key="allocation-mini-table-card"
      title="Allocation"
      dataTestId="allocation-view-card">
      <div
        className="w-full p-4"
        data-testid="allocation-mini-table-container">
        {loading && <ComponentSpinner label="Loading Allocation Data" />}
        {!loading && (
          <BasicDataGrid
            rows={records}
            columns={gridColumns}
            sxStyle={sxStyle}
            disableColumnMenu
            showColumnVerticalBorder
            showCellVerticalBorder
            primaryKey="linkId"
            dataTestId="allocation-table"
            pinnedColumns={{ left: ['details', firstColumnData.field, 'linkId'], right: [] }}
          />
        )}
      </div>
    </DetailViewCard>
  );
};
